import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { productsService } from '../services/productsService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { ProductCategory } from 'public-contracts';

const ModernProductCategoryForm: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allCategories, setAllCategories] = useState<ProductCategory[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    parentId: undefined as string | undefined,
  });

  useEffect(() => {
    loadAllCategories();
    if (isEdit && id) {
      loadCategory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadAllCategories = async () => {
    try {
      const categoriesData = await productsService.getProductCategories({
        page: 1,
        perPage: 1000, // Load all categories
      });
      const categoriesArray = Array.isArray(categoriesData)
        ? categoriesData
        : [];
      setAllCategories(categoriesArray);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadCategory = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const category = await productsService.getProductCategory(id);
      if (category) {
        setFormData({
          name: category.name || '',
          parentId: category.parentId || undefined,
        });
      } else {
        setError(translate('pos.product_categories.load_error'));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.product_categories.load_error')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Prepare data, only include parentId if it's set
      const submitData: { name: string; parentId?: string } = {
        name: formData.name,
      };
      if (formData.parentId) {
        submitData.parentId = formData.parentId;
      }

      if (isEdit && id) {
        await productsService.updateProductCategory(id, submitData);
      } else {
        await productsService.createProductCategory(submitData);
      }
      navigate('/product_categories');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.product_categories.save_error')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Filter out current category and its descendants to prevent circular references
  const availableParentCategories = useMemo(() => {
    if (!isEdit || !id) {
      return allCategories;
    }

    // Find all descendant IDs (categories that have current category in ancestorIds)
    const descendantIds = new Set<string>();
    const findDescendants = (categoryId: string) => {
      allCategories.forEach((cat) => {
        if (cat.ancestorIds?.includes(categoryId) || cat.parentId === categoryId) {
          descendantIds.add(cat.id);
          findDescendants(cat.id);
        }
      });
    };
    findDescendants(id);
    descendantIds.add(id); // Also exclude the current category itself

    return allCategories.filter((cat) => !descendantIds.has(cat.id));
  }, [allCategories, isEdit, id]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.product_categories.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/product_categories')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {translate('pos.common.action.back')}
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit
              ? translate('pos.common.action.edit')
              : translate('pos.product_categories.new_category')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit
              ? translate('pos.product_categories.edit_subtitle')
              : translate('pos.product_categories.create_subtitle')}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>
              {translate('pos.product_categories.category_details')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {translate('pos.productCategories.fields.name')} *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={translate(
                  'pos.product_categories.name_placeholder'
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="parentId">
                {translate(
                  'pos.productCategories.fields.parentId',
                  'Parent Category'
                )}
              </Label>
              <Select
                value={formData.parentId || 'none'}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    parentId: value === 'none' ? undefined : value,
                  }))
                }
              >
                <SelectTrigger id="parentId">
                  <SelectValue
                    placeholder={translate(
                      'pos.productCategories.fields.parentIdPlaceholder',
                      'Select parent category (optional)'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {translate('common.none', 'None (Root Category)')}
                  </SelectItem>
                  {availableParentCategories.map((category) => {
                    // Show indentation based on level
                    const indent = category.level ? '  '.repeat(category.level) : '';
                    return (
                      <SelectItem key={category.id} value={category.id}>
                        {indent}
                        {category.name}
                        {category.level !== undefined && category.level > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (L{category.level})
                          </span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {translate(
                  'pos.productCategories.fields.parentIdHelp',
                  'Select a parent category to create a hierarchical structure. Leave empty for a root category.'
                )}
              </p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving
                  ? translate('common.saving')
                  : isEdit
                  ? translate('pos.common.action.save')
                  : translate('pos.common.action.create')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/product_categories')}
              >
                {translate('pos.common.action.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default ModernProductCategoryForm;
